import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Search, RotateCcw } from 'lucide-react';
import formatCurrency from '../helper/formatCurrency';
import CustomLoader from './CustomLoader';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface WinLossItem {
  id: number;
  username: string;
  total_deposit: number;
  total_withdrawal: number;
  bet_amount: number;
  rolling_amount: number;
  win_loss: number;
  branch_username?: string;
  agent_username?: string;
}

const WinLossReportPlayer: React.FC = () => {
  const { user } = useAuth();
  const [dataList, setDataList] = useState<WinLossItem[]>([]);
  const [totals, setTotals] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Default dates: today YYYY-MM-DD
  const getTodayString = () => {
    return new Date().toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState<string>(getTodayString());
  const [endDate, setEndDate] = useState<string>(getTodayString());

  const fetchWinLoss = async () => {
    setLoading(true);
    try {
      // Append time bounds to date search:
      const start = startDate ? `${startDate} 00:00:00` : undefined;
      const end = endDate ? `${endDate} 23:59:59` : undefined;

      const res = await api.get('/win-loss-report-player', {
        params: {
          page,
          limit,
          search: searchTerm.trim() || undefined,
          startDate: start,
          endDate: end,
        },
      });

      if (res.data.status) {
        setDataList(res.data.data);
        setTotals(res.data.totals || null);
        setTotalPages(res.data.pagination.totalPages);
        setTotalCount(res.data.pagination.totalRecords);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch Win/Loss report.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWinLoss();
  }, [page, limit]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchWinLoss();
  };

  const handleReset = () => {
    setSearchTerm('');
    setStartDate(getTodayString());
    setEndDate(getTodayString());
    setPage(1);
  };

  const calculateRTP = (item: any) => {
    const rolling = Number(item.rolling_amount);
    const winLoss = Number(item.win_loss);
    if (rolling <= 0) return '0%';
    const pct = (winLoss / rolling) * 100;
    return `${pct.toFixed(2)}%`;
  };

  const getRTPColor = (item: any) => {
    const rolling = Number(item.rolling_amount);
    const winLoss = Number(item.win_loss);
    if (rolling <= 0 || winLoss === 0) return 'text-gray-900';
    return winLoss > 0 ? 'text-blue-600' : 'text-red-600';
  };

  const getPaginationGroup = () => {
    const range: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        range.push(i);
      }
    } else {
      if (page <= 4) {
        for (let i = 1; i <= 5; i++) {
          range.push(i);
        }
        range.push('...');
        range.push(totalPages);
      } else if (page >= totalPages - 3) {
        range.push(1);
        range.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          range.push(i);
        }
      } else {
        range.push(1);
        range.push('...');
        for (let i = page - 1; i <= page + 1; i++) {
          range.push(i);
        }
        range.push('...');
        range.push(totalPages);
      }
    }
    return range;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Win/Loss Report Player</h1>
        <p className="text-sm text-gray-500">
          Home / Win/Loss Report Player
        </p>
      </div>

      {/* Filters Form */}
      <form onSubmit={handleSearchSubmit} className="bg-white p-6 rounded-xl border space-y-4 shadow-sm max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* From Date */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* To Date */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Username Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Username</label>
            <input
              type="text"
              placeholder="Username"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-start gap-2 pt-2">
          <button
            type="submit"
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold text-sm transition flex items-center gap-1.5 shadow-sm"
          >
            <Search className="w-4 h-4" />
            Search
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-5 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded font-semibold text-sm transition flex items-center gap-1.5 shadow-sm"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </form>

      {/* Show entries dropdown & Info block */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>Show</span>
        <select
          value={limit}
          onChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(1);
          }}
          className="rounded border border-gray-300 px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span>entries</span>
      </div>

      {/* Main Table */}
      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm truncate">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Username
              </th>
              {user?.role === "admin" && (
                <>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Agent
                  </th>
                </>
              )}
              {user?.role === "backoffice" && (
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Agent
                </th>
              )}
              {[
                'Total deposit',
                'Total withdrawal',
                'Bet Amount',
                'Rolling Amount',
                'Win/Loss',
                'RTP',
              ].map((h) => (
                <th key={h} className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={7 + (user?.role === "admin" ? 2 : user?.role === "backoffice" ? 1 : 0)} className="text-center py-12">
                  <div className="flex justify-center items-center">
                    <CustomLoader />
                  </div>
                </td>
              </tr>
            ) : dataList.length === 0 ? (
              <tr>
                <td colSpan={7 + (user?.role === "admin" ? 2 : user?.role === "backoffice" ? 1 : 0)} className="text-center py-12 text-gray-500 font-semibold">
                  No records found.
                </td>
              </tr>
            ) : (
              dataList.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50/50 transition">
                  {/* Clickable Username */}
                  <td className="px-6 py-4">
                    <Link
                      to={`/users/profile/${item.id}`}
                      className="text-blue-600 hover:text-blue-800 font-semibold hover:underline"
                    >
                      ({item.id}) {item.username}
                    </Link>
                  </td>
                  {user?.role === "admin" && (
                    <>
                      <td className="px-6 py-4 text-gray-700 font-semibold">
                        {item.branch_username || "—"}
                      </td>
                      <td className="px-6 py-4 text-gray-700 font-semibold">
                        {item.agent_username || "—"}
                      </td>
                    </>
                  )}
                  {user?.role === "backoffice" && (
                    <td className="px-6 py-4 text-gray-700 font-semibold">
                      {item.agent_username || "—"}
                    </td>
                  )}
                  <td className={`px-6 py-4 font-semibold ${Number(item.total_deposit) > 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                    {formatCurrency(Number(item.total_deposit))}
                  </td>
                  <td className={`px-6 py-4 font-semibold ${Number(item.total_withdrawal) > 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                    {formatCurrency(Number(item.total_withdrawal))}
                  </td>
                  <td className={`px-6 py-4 font-semibold ${Number(item.bet_amount) > 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                    {formatCurrency(Number(item.bet_amount))}
                  </td>
                  <td className={`px-6 py-4 font-semibold ${Number(item.rolling_amount) > 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                    {formatCurrency(Number(item.rolling_amount))}
                  </td>
                  <td
                    className={`px-6 py-4 font-semibold ${
                      Number(item.win_loss) > 0 ? 'text-blue-600' : Number(item.win_loss) < 0 ? 'text-red-600' : 'text-gray-900'
                    }`}
                  >
                    {formatCurrency(Number(item.win_loss))}
                  </td>
                  <td className={`px-6 py-4 font-bold ${getRTPColor(item)}`}>
                    {calculateRTP(item)}
                  </td>
                </tr>
              ))
            )}
          </tbody>

          {totals && dataList.length > 0 && (
            <tfoot className="bg-gray-50 border-t font-bold text-gray-900">
              <tr>
                <td colSpan={1 + (user?.role === "admin" ? 2 : user?.role === "backoffice" ? 1 : 0)} className="px-6 py-4 font-bold text-right pr-12">Total:</td>
                <td className="px-6 py-4 text-blue-600 font-semibold">
                  {formatCurrency(Number(totals.total_deposit))}
                </td>
                <td className="px-6 py-4 text-blue-600 font-semibold">
                  {formatCurrency(Number(totals.total_withdrawal))}
                </td>
                <td className="px-6 py-4 text-blue-600 font-semibold">
                  {formatCurrency(Number(totals.bet_amount))}
                </td>
                <td className="px-6 py-4 text-blue-600 font-semibold">
                  {formatCurrency(Number(totals.rolling_amount))}
                </td>
                <td
                  className={`px-6 py-4 font-semibold ${
                    Number(totals.win_loss) >= 0 ? 'text-blue-600' : 'text-red-600'
                  }`}
                >
                  {formatCurrency(Number(totals.win_loss))}
                </td>
                <td
                  className={`px-6 py-4 font-bold ${
                    Number(totals.win_loss) >= 0 ? 'text-blue-600' : 'text-red-600'
                  }`}
                >
                  {Number(totals.rolling_amount) > 0
                    ? `${((Number(totals.win_loss) / Number(totals.rolling_amount)) * 100).toFixed(2)}%`
                    : '0%'}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 text-sm text-gray-600">
        <div>
          Showing {totalCount === 0 ? 0 : (page - 1) * limit + 1} to {Math.min(page * limit, totalCount)} of {totalCount.toLocaleString()} entries (filtered from {totalCount.toLocaleString()} total entries)
        </div>

        {totalPages > 1 && (
          <div className="flex items-center border rounded-md overflow-hidden bg-white shadow-sm">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-2 border-r hover:bg-gray-50 transition text-sm font-semibold disabled:opacity-50 disabled:hover:bg-transparent"
            >
              Previous
            </button>

            {getPaginationGroup().map((item, idx) => {
              if (item === '...') {
                return (
                  <span key={`ellipsis-${idx}`} className="px-3 py-2 border-r bg-gray-50 text-gray-400">
                    ...
                  </span>
                );
              }
              return (
                <button
                  key={`page-${item}`}
                  onClick={() => setPage(Number(item))}
                  className={`px-3.5 py-2 border-r hover:bg-gray-50 transition text-sm font-semibold ${
                    page === item ? 'bg-blue-600 text-white hover:bg-blue-600' : 'text-blue-600'
                  }`}
                >
                  {item}
                </button>
              );
            })}

            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-2 hover:bg-gray-50 transition text-sm font-semibold disabled:opacity-50 disabled:hover:bg-transparent"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WinLossReportPlayer;
